<template>
    <div class="table-container">
        <div class="header-block">
            <div class="search-wrapper">
                <v-text-field
                    class="region-search"
                    v-model="countrySearchLocal"
                    label="Страна"
                    placeholder="Начните вводить страну"
                    clearable
                    @input="applyCountrySearch"
                />
            </div>
        </div>

        <!-- Обертка для вертикального скролла -->
        <div class="vertical-scroll-container">
            <!-- Заголовок с логотипами компаний -->
            <div class="companies-header">
                <div class="region-field">Страны</div>
                <div v-for="company in value.companies" 
                     :key="company.alias + company.logo" 
                     class="small-field company-logo">
                    <div v-if="company.logo" class="logo-container">
                        <img
                            :alt="company.name.split(' ').slice(0, 4).join(' ')"
                            :src="getUrl(company.logo)"
                            class="company-logo-img"
                            @error="handleImageError"
                        />
                    </div>
                    <div v-else class="placeholder-logo">
                        <span>{{ getInitials(company.name) }}</span>
                    </div>
                    <span class="company-subtext">{{ formatText(company.subtext ? company.subtext : company.name) }}</span>
                </div>
            </div>
        
            <!-- Тело таблицы -->
            <div class="table-body">
                <div v-if="value.canEdit">
                    <div class="regions" v-for="country in value.countries" 
                         :key="country.id" v-show="country.show">
                        <div class="region-field">
                            <span>{{ country.val }}</span>
                        </div>
                        <div v-for="company in value.companies" 
                             :key="company.alias + company.logo" 
                             class="small-field">
                            <v-text-field
                                :rules="[rules.commission]"
                                validate-on-blur
                                :value="getCommissionValue(company.alias, country.id)"
                                @input="setCommissionValue(company.alias, country.id, $event)"
                            ></v-text-field>
                        </div>
                    </div>
                </div>

                <div v-if="!value.canEdit">
                    <div class="regions cant-edit-region" 
                         v-for="country in value.countries" 
                         :key="country.id" v-show="country.show">
                        <div class="region-field">
                            <span>{{ country.val }}</span>
                        </div>
                        <div v-for="company in value.companies" 
                             :key="company.alias + country.id"
                             class="small-field cant-edit-field">
                            <span>{{ getCommissionValue(company.alias, country.id) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        canEdit: {
            type: Boolean,
            default: false
        },
        // опциональные данные, если приходят из родителя (как в Commissions.vue)
        regions: {
            type: [Array, String],
            default: () => []
        },
        companies: {
            type: Array,
            default: () => []
        },
        commissions: {
            type: [Object, String],
            default: () => ({})
        },
        templates: {
            type: Array,
            default: () => []
        },
        templateId: {
            type: [Number, String],
            default: null
        },
        templateName: {
            type: String,
            default: 'Шаблон комиссий транзит'
        },
        countrySearch: {
            type: String,
            default: ''
        }
    },
    data: () => ({
        searching: false,
        countrySearchLocal: '',
        isUpdatingCommissions: false, // Флаг для предотвращения циклических обновлений
        value: {
            canEdit: false,
            countries: [],
            companies: [],
            commissions: {},
        },
        rules: {
            commission: v => ((/^[0-9]+/.test(v) && v <= 100) || v ==='') || 'Ошибка',
        }
    }),
    methods: {
        getUrl(path) {
            return path ? `/${path}` : '';
        },
        formatText(text) {
            if (!text) return '';
            
            // Замена слов "двадцать первый" на "21"
            const replacements = {
                'Двадцать первый': '21',
                // Можно добавить другие замены при необходимости
            };
            
            let formattedText = text;
            for (const [key, value] of Object.entries(replacements)) {
                const regex = new RegExp(key, 'gi');
                formattedText = formattedText.replace(regex, value);
            }
            
            return formattedText;
        },
        handleImageError(event) {
            const parent = event.target.parentElement;
            if (parent) {
                // Создаем элемент-заглушку
                const placeholder = document.createElement('div');
                placeholder.className = 'placeholder-logo';
                
                // Получаем инициалы из alt-текста
                const altText = event.target.alt || '';
                const words = altText.split(' ');
                const initials = words.slice(0, 2).map(w => w.charAt(0)).join('');
                
                placeholder.innerHTML = `<span>${initials}</span>`;
                
                // Заменяем изображение на заглушку
                parent.replaceChild(placeholder, event.target);
            }
        },
        getInitials(name) {
            if (!name) return '';
            const words = name.split(' ');
            return words.slice(0, 2).map(w => w.charAt(0)).join('');
        },
        async fetchCountries() {
            try {
                const response = await axios.get('/transit/get-countries-list-for-commission');
                const countriesData = typeof response.data === 'string' 
                    ? JSON.parse(response.data) 
                    : response.data;
                
                // Преобразуем массив стран в объекты с id и val
                this.value.countries = Array.isArray(countriesData)
                    ? countriesData.map((name, index) => ({
                        id: index.toString(),
                        val: name,
                        show: true
                    }))
                    : Object.entries(countriesData).map(([id, name]) => ({
                        id: id.toString(),
                        val: name,
                        show: true
                    }));
            } catch (error) {
                this.value.countries = [];
            }
        },
        async fetchCompanies() {
            try {
                const response = await axios.get('/api/transit/dictionary/companies');
                let companiesData = response.data;
                
                // Если ответ в формате { data: ... }, извлекаем data
                if (companiesData && companiesData.data) {
                    companiesData = typeof companiesData.data === 'string' 
                        ? JSON.parse(companiesData.data) 
                        : companiesData.data;
                } else if (typeof companiesData === 'string') {
                    companiesData = JSON.parse(companiesData);
                }
                
                // Преобразуем данные компаний в нужный формат
                const companiesArray = Array.isArray(companiesData) 
                    ? companiesData
                    : Object.values(companiesData);
                
                this.value.companies = companiesArray.map(company => ({
                    alias: company.alias || company.company_alias,
                    name: company.name || company.company_name,
                    logo: company.logo || company.company_logo || company.image,
                    subtext: company.subtext || company.company_subtext || company.name
                }));
            } catch (error) {
                this.value.companies = [];
            }
        },
        async fetchTemplate() {
            if (!this.templateId) {
                // Инициализируем пустую структуру комиссий
                this.initializeCommissions();
                return;
            }

            try {
                const response = await axios.get(`/transit/get-transit-commission-template/${this.templateId}`);
                let templateData = response.data;
                
                // Если ответ в формате строки, парсим JSON
                if (typeof templateData === 'string') {
                    templateData = JSON.parse(templateData);
                }
                
                if (templateData && typeof templateData === 'object') {
                    // Преобразуем структуру данных
                    const convertedCommissions = {};
                    
                    this.value.companies.forEach(company => {
                        convertedCommissions[company.alias] = {};
                        
                        if (templateData[company.alias]) {
                            const companyData = templateData[company.alias];
                            
                            // Проверяем формат данных: TRANSIT или по странам
                            if (companyData.TRANSIT !== undefined) {
                                // Формат TRANSIT: одно значение для всех стран
                                const transitValue = companyData.TRANSIT;
                                this.value.countries.forEach(country => {
                                    // Сохраняем существующее значение (включая 0 и пустую строку)
                                    // Заменяем только null/undefined
                                    convertedCommissions[company.alias][country.id] = 
                                        (transitValue !== null && transitValue !== undefined) ? String(transitValue) : '0';
                                });
                            } else {
                                // Формат по странам: находим страну по названию
                                this.value.countries.forEach(country => {
                                    if (companyData[country.val] !== undefined) {
                                        const value = companyData[country.val];
                                        // Сохраняем существующее значение (включая 0 и пустую строку)
                                        // Заменяем только null/undefined
                                        convertedCommissions[company.alias][country.id] = 
                                            (value !== null && value !== undefined) ? String(value) : '0';
                                    } else {
                                        convertedCommissions[company.alias][country.id] = '0';
                                    }
                                });
                            }
                        } else {
                            // Если для компании нет данных, заполняем нулями
                            this.value.countries.forEach(country => {
                                convertedCommissions[company.alias][country.id] = '0';
                            });
                        }
                    });
                    
                    this.value.commissions = convertedCommissions;
                } else {
                    this.initializeCommissions();
                }
            } catch (error) {
                this.initializeCommissions();
            }
        },
        initializeCommissions() {
            // Проверяем, что страны и компании загружены
            if (!this.value.countries.length || !this.value.companies.length) {
                return;
            }
            
            const commissions = {};
            this.value.companies.forEach(company => {
                commissions[company.alias] = {};
                this.value.countries.forEach(country => {
                    // Инициализируем нулями, если значение не задано
                    commissions[company.alias][country.id] = '0';
                });
            });
            this.value.commissions = commissions;
        },
        initializeCommissionsAndReturn() {
            // Вспомогательный метод для возврата структуры комиссий
            if (!this.value.countries.length || !this.value.companies.length) {
                return {};
            }
            
            const commissions = {};
            this.value.companies.forEach(company => {
                commissions[company.alias] = {};
                this.value.countries.forEach(country => {
                    commissions[company.alias][country.id] = '0';
                });
            });
            return commissions;
        },
        normalizeCountryNamesToIds(source) {
            // Преобразуем структуру company -> { 'АБХАЗИЯ': '31', ... } в company -> country.id
            if (!source || typeof source !== 'object' || !this.value.countries.length || !this.value.companies.length) {
                return null;
            }

            const converted = {};
            this.value.companies.forEach(company => {
                converted[company.alias] = {};
                const companyData = source[company.alias] || {};

                this.value.countries.forEach(country => {
                    if (companyData.hasOwnProperty(country.val)) {
                        const value = companyData[country.val];
                        // Сохраняем значение, если оно есть (включая 0 и пустую строку)
                        converted[company.alias][country.id] = (value !== null && value !== undefined) ? value : '0';
                    } else {
                        converted[company.alias][country.id] = '0';
                    }
                });
            });
            return converted;
        },
        normalizeTransitToCountries(source) {
            // Преобразуем структуру company -> TRANSIT в company -> country.id
            if (!source || typeof source !== 'object' || !this.value.countries.length || !this.value.companies.length) {
                return null;
            }

            const converted = {};
            this.value.companies.forEach(company => {
                converted[company.alias] = {};
                const companyData = source[company.alias];
                const transitValue = companyData && companyData.TRANSIT !== undefined ? companyData.TRANSIT : '0';
                this.value.countries.forEach(country => {
                    // Сохраняем значение, если оно есть (включая 0 и пустую строку)
                    converted[company.alias][country.id] = (transitValue !== null && transitValue !== undefined) ? transitValue : '0';
                });
            });
            return converted;
        },
        applyCountrySearch() {
            this.searching = true;
            const term = (this.countrySearchLocal || '').toLowerCase();
            this.value.countries.forEach(country => {
                country.show = !term || country.val.toLowerCase().includes(term);
            });
            this.searching = false;
        },
        getCommissionValue(companyAlias, countryId) {
            if (!this.value.commissions || !this.value.commissions[companyAlias]) {
                return '0';
            }
            const value = this.value.commissions[companyAlias][countryId];
            return (value === '' || value === null || value === undefined) ? '0' : value;
        },
        setCommissionValue(companyAlias, countryId, value) {
            if (!this.value.commissions) {
                this.$set(this.value, 'commissions', {});
            }
            if (!this.value.commissions[companyAlias]) {
                this.$set(this.value.commissions, companyAlias, {});
            }
            this.$set(this.value.commissions[companyAlias], countryId, value);
        },
        processCommissionsData(externalCommissions) {
            if (!externalCommissions || !Object.keys(externalCommissions).length) {
                this.isUpdatingCommissions = true;
                this.initializeCommissions();
                this.$nextTick(() => {
                    this.isUpdatingCommissions = false;
                });
                return;
            }
            
            const firstCompany = this.value.companies[0];
            const firstCountry = this.value.countries[0];
            const firstCompanyData = externalCommissions[firstCompany.alias];
            
            this.isUpdatingCommissions = true;
            
            if (firstCompanyData && Array.isArray(firstCompanyData)) {
                const converted = {};
                this.value.companies.forEach(company => {
                    converted[company.alias] = {};
                    const companyData = externalCommissions[company.alias] || [];
                    this.value.countries.forEach((country, index) => {
                        const value = companyData[index];
                        const countryIdStr = String(country.id);
                        if (value !== undefined && value !== null) {
                            converted[company.alias][countryIdStr] = String(value);
                        } else {
                            converted[company.alias][countryIdStr] = '0';
                        }
                    });
                });
                this.$set(this.value, 'commissions', converted);
            } else if (externalCommissions[firstCompany.alias] && 
                externalCommissions[firstCompany.alias][firstCountry.id] !== undefined) {
                const filledCommissions = {};
                this.value.companies.forEach(company => {
                    filledCommissions[company.alias] = {};
                    const companyData = externalCommissions[company.alias] || {};
                    this.value.countries.forEach(country => {
                        const value = companyData[country.id];
                        filledCommissions[company.alias][country.id] = 
                            (value !== null && value !== undefined) ? value : '0';
                    });
                });
                this.$set(this.value, 'commissions', filledCommissions);
            } else {
                const normalized = this.normalizeTransitToCountries(externalCommissions) || 
                                 this.normalizeCountryNamesToIds(externalCommissions);
                if (normalized) {
                    this.value.companies.forEach(company => {
                        this.value.countries.forEach(country => {
                            if (!normalized[company.alias]) {
                                normalized[company.alias] = {};
                            }
                            if (normalized[company.alias][country.id] === null || 
                                normalized[company.alias][country.id] === undefined) {
                                normalized[company.alias][country.id] = '0';
                            }
                        });
                    });
                }
                this.$set(this.value, 'commissions', normalized || this.initializeCommissionsAndReturn());
            }
            
            this.$nextTick(() => {
                this.isUpdatingCommissions = false;
            });
        }
    },
    async created() {
        this.value.canEdit = this.canEdit;
        this.countrySearchLocal = this.countrySearch || '';

        const hasExternalRegions = (Array.isArray(this.regions) && this.regions.length > 0) ||
            (typeof this.regions === 'string' && this.regions.length > 0);
        const hasExternalCompanies = Array.isArray(this.companies) && this.companies.length > 0;
        
        const externalCommissions = typeof this.commissions === 'string'
            ? JSON.parse(this.commissions || '{}')
            : (this.commissions || {});

        if (hasExternalRegions) {
            const regionsParsed = typeof this.regions === 'string' ? JSON.parse(this.regions) : this.regions;
            this.value.countries = regionsParsed.map(r => ({
                id: r.id.toString(),
                val: r.val,
                show: true,
            }));
        }

        if (hasExternalCompanies) {
            this.value.companies = this.companies.map(c => ({
                alias: c.alias || c.company_alias,
                name: c.name || c.company_name,
                logo: c.logo || c.company_logo || c.image,
                subtext: c.subtext || c.company_subtext || c.name
            }));
        }

        if (!this.value.countries.length || !this.value.companies.length) {
            await Promise.all([
                this.value.countries.length ? Promise.resolve() : this.fetchCountries(),
                this.value.companies.length ? Promise.resolve() : this.fetchCompanies()
            ]);
        }

        // После загрузки countries и companies, обрабатываем комиссии вручную
        // потому что watcher может сработать до загрузки данных
        if (this.value.countries.length && this.value.companies.length) {
            // Принудительно обрабатываем данные через watcher логику
            this.processCommissionsData(externalCommissions);
        }

        if (this.templateId && (!externalCommissions || !Object.keys(externalCommissions).length)) {
            await this.fetchTemplate();
        }
    },
    watch: {
        countrySearch() {
            if (this.searching) {
                return;
            }
            this.countrySearchLocal = this.countrySearch || '';
            this.applyCountrySearch();
        },
        templateId: {
            handler() {
                this.fetchTemplate();
            }
        },
        commissions: {
            handler(newCommissions) {
                // Предотвращаем циклические обновления
                if (this.isUpdatingCommissions) {
                    return;
                }
                
                // Если страны и компании еще не загружены, не обрабатываем
                if (!this.value.countries.length || !this.value.companies.length) {
                    return;
                }
                
                const externalCommissions = typeof newCommissions === 'string'
                    ? JSON.parse(newCommissions || '{}')
                    : (newCommissions || {});
                
                // Если данных нет или они пустые, инициализируем нулями
                if (!externalCommissions || !Object.keys(externalCommissions).length) {
                    this.isUpdatingCommissions = true;
                    this.initializeCommissions();
                    this.$nextTick(() => {
                        this.isUpdatingCommissions = false;
                    });
                    return;
                }
                
                const firstCompany = this.value.companies[0];
                const firstCountry = this.value.countries[0];
                const firstCompanyData = externalCommissions[firstCompany.alias];
                
                this.isUpdatingCommissions = true;
                
                // Проверяем формат данных: массив или объект
                if (firstCompanyData && Array.isArray(firstCompanyData)) {
                    // Данные в формате массива [value0, value1, ...] - конвертируем в объект с countryId
                    const converted = {};
                    this.value.companies.forEach(company => {
                        converted[company.alias] = {};
                        const companyData = externalCommissions[company.alias] || [];
                        this.value.countries.forEach((country, index) => {
                            const value = companyData[index];
                            const countryIdStr = String(country.id);
                            if (value !== undefined && value !== null) {
                                converted[company.alias][countryIdStr] = String(value);
                            } else {
                                converted[company.alias][countryIdStr] = '0';
                            }
                        });
                    });
                    this.$set(this.value, 'commissions', converted);
                } else if (externalCommissions[firstCompany.alias] && 
                    externalCommissions[firstCompany.alias][firstCountry.id] !== undefined) {
                    // Данные уже в правильном формате, заполняем только отсутствующие значения нулями
                    const filledCommissions = {};
                    this.value.companies.forEach(company => {
                        filledCommissions[company.alias] = {};
                        const companyData = externalCommissions[company.alias] || {};
                        this.value.countries.forEach(country => {
                            const value = companyData[country.id];
                            filledCommissions[company.alias][country.id] = 
                                (value !== null && value !== undefined) ? value : '0';
                        });
                    });
                    this.$set(this.value, 'commissions', filledCommissions);
                } else {
                    const normalized = this.normalizeTransitToCountries(externalCommissions) || 
                                     this.normalizeCountryNamesToIds(externalCommissions);
                    if (normalized) {
                        // Заполняем только отсутствующие значения нулями (не заменяем существующие)
                        this.value.companies.forEach(company => {
                            this.value.countries.forEach(country => {
                                if (!normalized[company.alias]) {
                                    normalized[company.alias] = {};
                                }
                                // Заменяем только null/undefined, сохраняем все остальные значения (включая 0 и '')
                                if (normalized[company.alias][country.id] === null || 
                                    normalized[company.alias][country.id] === undefined) {
                                    normalized[company.alias][country.id] = '0';
                                }
                            });
                        });
                    }
                    const finalCommissions = normalized || this.initializeCommissionsAndReturn();
                    this.$set(this.value, 'commissions', finalCommissions);
                }
                
                this.$nextTick(() => {
                    this.isUpdatingCommissions = false;
                });
            },
            immediate: true,
            deep: true
        },
        'value.commissions': {
            handler(newVal) {
                // Предотвращаем эмит во время обновления из prop
                if (!this.isUpdatingCommissions) {
                    // Отдаем наверх актуальные комиссии (по country.id)
                    this.$emit('update:commissions', newVal);
                }
            },
            deep: true
        }
    },
}
</script>

<style scoped>
/* Основные стили без изменений */
.table-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 80vh;
    overflow: hidden;
}

.header-block {
    padding: 10px 0;
}

.search-wrapper {
    max-width: 360px;
    width: 100%;
}

.region-search {
    max-width: 360px;
    width: 100%;
}

.vertical-scroll-container {
    overflow-y: auto;
    height: 100%;
}

.companies-header {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
    min-width: fit-content;
}

.region-field {
    position: sticky;
    left: 0;
    width: 240px;
    height: 100px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-shrink: 0;
    background: white;
    padding-left: 12px;
    z-index: 90;
    font-size: 14px;
}

.companies-header .region-field {
    z-index: 110;
}

.table-body .region-field {
    z-index: 80;
}

.table-body {
    min-width: fit-content;
}

.small-field {
    flex-shrink: 0;
    height: 100px;
    width: 110px;
    padding: 0 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.company-logo {
    height: 100px;
    padding-bottom: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
}

/* Стили для изображений компаний */
.company-logo-img {
    object-fit: contain;
    max-width: 100%;
    margin-top: 8px;
    height: auto;
    display: block;
}

.logo-container {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Стили для заглушки */
.placeholder-logo {
    width: 60px;
    height: 40px;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #666;
}

/* Медиа-запросы для адаптации заглушки */
@media (max-width: 992px) {
    .placeholder-logo {
        width: 50px;
        height: 35px;
        font-size: 14px;
    }
}

@media (max-width: 768px) {
    .placeholder-logo {
        width: 40px;
        height: 30px;
        font-size: 12px;
    }
}

@media (max-width: 576px) {
    .placeholder-logo {
        width: 35px;
        height: 25px;
        font-size: 11px;
    }
}

@media (max-width: 480px) {
    .placeholder-logo {
        width: 30px;
        height: 20px;
        font-size: 10px;
    }
}

.company-subtext {
    height: 50px;
    font-size: 12px;
    margin-top: 10px;
}

.cant-edit-field {
    line-height: 100px;
    text-align: center;
}

.regions {
    display: flex;
    min-width: fit-content;
    border-bottom: solid 1px #eeeeee;
}

.cant-edit-region {
    min-height: 100px;
    display: flex;
}

/* Медиа-запросы для адаптации */
@media (max-width: 1200px) {
    .region-field {
        width: 200px;
        height: 90px;
        font-size: 13px;
    }
    .small-field {
        width: 100px;
        height: 90px;
    }
    .company-logo-img {
        height: 40px;
        width: 70px;
    }
    .company-subtext {
        font-size: 11px;
    }
}

@media (max-width: 992px) {
    .region-field {
        width: 180px;
        height: 85px;
        font-size: 12px;
    }
    .small-field {
        width: 90px;
        height: 85px;
    }
    .company-logo-img {
        height: 35px;
        width: 60px;
    }
    .company-subtext {
        font-size: 10px;
    }
}

@media (max-width: 768px) {
    .table-container {
        height: 70vh;
    }
    
    .region-field {
        width: 160px;
        height: 80px;
        font-size: 11px;
        padding-left: 8px;
    }
    .small-field {
        width: 80px;
        height: 80px;
        padding: 0 5px;
    }
    .company-logo-img {
        height: 30px;
        width: 50px;
    }
    .company-subtext {
        font-size: 9px;
        margin-top: 2px;
    }
    .cant-edit-field {
        line-height: 80px;
        font-size: 12px;
    }
    
    /* Уменьшаем размер текста в полях ввода */
    .v-text-field >>> input {
        font-size: 12px !important;
    }
    .v-text-field >>> .v-label {
        font-size: 12px !important;
    }
}

@media (max-width: 576px) {
    .region-field {
        width: 140px;
        height: 70px;
        font-size: 10px;
    }
    .small-field {
        width: 70px;
        height: 70px;
    }
    .company-logo-img {
        height: 25px;
        width: 40px;
    }
    .company-subtext {
        font-size: 8px;
    }
    .cant-edit-field {
        line-height: 70px;
        font-size: 11px;
    }
}

/* Для очень маленьких экранов */
@media (max-width: 480px) {
    .region-field {
        width: 120px;
        height: 65px;
        font-size: 9px;
    }
    .small-field {
        width: 65px;
        height: 65px;
    }
    .company-logo-img {
        height: 20px;
        width: 35px;
    }
    .company-subtext {
        font-size: 7px;
    }
    .cant-edit-field {
        line-height: 65px;
        font-size: 10px;
    }
    
    /* Уменьшаем отступы */
    .companies-header {
        padding-bottom: 5px;
    }
    .region-field {
        padding-left: 5px;
    }
}
</style>



// WEBPACK FOOTER //
// resources/js/components/b2b/CommissionsTransit.vue